import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

export type ResizeMode = 'auto' | 'manual';

interface Options {
  storageKey: string;
  min: number;
  max: number;
  defaultWidth: number;
  enabled: boolean;
  contentSignature?: string | number;
}

interface Result {
  width: number;
  mode: ResizeMode;
  dragging: boolean;
  asideRef: (el: HTMLElement | null) => void;
  measureRef: (el: HTMLElement | null) => void;
  onHandlePointerDown: (e: ReactPointerEvent) => void;
  onHandleDoubleClick: () => void;
  onHandleKeyDown: (e: KeyboardEvent) => void;
}

function readStoredWidth(key: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeStoredWidth(key: string, value: number) {
  try {
    window.localStorage.setItem(key, String(Math.round(value)));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

function clearStoredWidth(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function clampWidth(value: number, min: number, max: number): number {
  const viewportCap =
    typeof window !== 'undefined' ? Math.floor(window.innerWidth * 0.35) : max;
  const effectiveMax = Math.max(min, Math.min(max, viewportCap));
  return Math.max(min, Math.min(effectiveMax, Math.round(value)));
}

/**
 * Measures the widest `data-resizable-label` descendant under `list`, adds
 * chrome padding (computed from the row's box model) and scrollbar gutter,
 * and returns a clamped px width.
 *
 * Text width is measured via a detached ghost span that copies the label's
 * font properties. This is necessary because `label.scrollWidth` returns the
 * container width when the text fits (per the CSSOM spec — scrollWidth is
 * clamped to clientWidth when there's no overflow), so reading it directly
 * would cause the auto-fit to lock onto whatever width the column already
 * has rather than the text's true intrinsic width.
 */
function measureTextIntrinsicWidth(label: HTMLElement, ghost: HTMLSpanElement): number {
  const computed = window.getComputedStyle(label);
  ghost.style.font = computed.font;
  ghost.style.letterSpacing = computed.letterSpacing;
  ghost.style.textTransform = computed.textTransform;
  ghost.textContent = label.textContent ?? '';
  return ghost.getBoundingClientRect().width;
}

function measureContentWidth(list: HTMLElement, min: number, max: number): number {
  const labels = list.querySelectorAll<HTMLElement>('[data-resizable-label]');
  if (labels.length === 0) return clampWidth(min, min, max);

  const ghost = document.createElement('span');
  ghost.style.cssText =
    'position:absolute;left:-99999px;top:0;visibility:hidden;white-space:nowrap;pointer-events:none;';
  document.body.appendChild(ghost);

  let widest = 0;
  try {
    for (const label of labels) {
      const rect = label.getBoundingClientRect();
      const textWidth = measureTextIntrinsicWidth(label, ghost);
      const row = label.closest<HTMLElement>('[data-resizable-row]') ?? label;
      const rowRect = row.getBoundingClientRect();
      const labelLeft = rect.left - rowRect.left;
      const labelRight = rowRect.right - rect.right;
      const chrome = Math.max(0, labelLeft) + Math.max(0, labelRight);
      // +2px safety margin covers sub-pixel glyph rendering at HiDPI and
      // fractional `getBoundingClientRect` values that round to a pixel
      // boundary exactly equal to the fitted width (truncate triggers on
      // `>=`, not `>`).
      widest = Math.max(widest, Math.ceil(textWidth) + chrome + 2);
    }
  } finally {
    document.body.removeChild(ghost);
  }

  const scrollbarGutter = Math.max(0, list.offsetWidth - list.clientWidth);
  return clampWidth(widest + scrollbarGutter, min, max);
}

export function useResizableColumn(opts: Options): Result {
  const { storageKey, min, max, defaultWidth, enabled, contentSignature } = opts;

  // Mount always starts in auto mode and runs a measurement. Any stored width
  // that turns out to be narrower than the current content's auto-fit is
  // treated as stale (e.g. carried over from an earlier data state or from
  // the now-fixed first-drag bug) and purged so the column never boots into
  // a truncating state. Stored widths wider than auto-fit are honoured as a
  // deliberate "I want extra space" preference.
  const [mode, setMode] = useState<ResizeMode>('auto');
  const [width, setWidth] = useState<number>(() =>
    enabled ? clampWidth(defaultWidth, min, max) : defaultWidth,
  );
  const [dragging, setDragging] = useState(false);

  const asideElRef = useRef<HTMLElement | null>(null);
  const listElRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const mutationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const draggingRef = useRef(dragging);
  draggingRef.current = dragging;

  const measure = useCallback(() => {
    if (draggingRef.current) return;
    const list = listElRef.current;
    if (!list) return;
    const fitted = measureContentWidth(list, min, max);
    const stored = readStoredWidth(storageKey);
    if (stored != null && stored >= fitted) {
      setWidth(clampWidth(stored, min, max));
      setMode('manual');
    } else {
      setWidth(fitted);
      setMode('auto');
      if (stored != null) clearStoredWidth(storageKey);
    }
  }, [min, max, storageKey]);

  // Re-measure when the content signature changes (row count, title hash).
  useLayoutEffect(() => {
    if (!enabled) return;
    measure();
  }, [enabled, contentSignature, measure]);

  // Re-clamp when the window resizes (the 35% viewport cap depends on innerWidth).
  useEffect(() => {
    if (!enabled) return;
    function onResize() {
      measure();
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled, measure]);

  // Re-measure once web fonts have loaded — the first measurement can use
  // fallback-font metrics that are slightly narrower than the final face.
  useEffect(() => {
    if (!enabled) return;
    if (typeof document === 'undefined' || !document.fonts?.ready) return;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (cancelled) return;
      measure();
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, measure]);

  const asideRef = useCallback((el: HTMLElement | null) => {
    asideElRef.current = el;
  }, []);

  const measureRef = useCallback(
    (el: HTMLElement | null) => {
      listElRef.current = el;
      observerRef.current?.disconnect();
      observerRef.current = null;
      mutationObserverRef.current?.disconnect();
      mutationObserverRef.current = null;
      if (!el || !enabled) return;
      const ro = new ResizeObserver(() => {
        measure();
      });
      ro.observe(el);
      observerRef.current = ro;
      // Title text can change asynchronously after a row is already mounted —
      // e.g. `useLiveMeta` in the Documentation app's PageRow swaps in a Yjs
      // meta title when a remote peer renames the doc. That doesn't resize the
      // row's box (truncate keeps the label's width pinned to its flex parent),
      // so ResizeObserver won't fire and contentSignature from the snapshot
      // title is stale. Watch subtree text changes directly as a fallback.
      const mo = new MutationObserver(() => {
        if (mutationDebounceRef.current != null) return;
        mutationDebounceRef.current = setTimeout(() => {
          mutationDebounceRef.current = null;
          measure();
        }, 50);
      });
      mo.observe(el, { childList: true, subtree: true, characterData: true });
      mutationObserverRef.current = mo;
      measure();
    },
    [enabled, measure],
  );

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      mutationObserverRef.current?.disconnect();
      if (mutationDebounceRef.current != null) {
        clearTimeout(mutationDebounceRef.current);
      }
    },
    [],
  );

  const onHandlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (!enabled) return;
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = asideElRef.current?.getBoundingClientRect().width ?? width;
      let latest = startW;
      // `draggingRef` suppresses measure() so the aside's resize during drag
      // doesn't trigger auto-fit via the ResizeObserver / MutationObserver —
      // without it the first drag fights the auto-measurement and snaps back.
      draggingRef.current = true;
      setDragging(true);
      modeRef.current = 'manual';
      setMode('manual');
      const prevCursor = document.body.style.cursor;
      const prevSelect = document.body.style.userSelect;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      function onMove(ev: PointerEvent) {
        latest = clampWidth(startW + (ev.clientX - startX), min, max);
        setWidth(latest);
      }
      function onUp() {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevSelect;
        writeStoredWidth(storageKey, latest);
        draggingRef.current = false;
        setDragging(false);
      }
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [enabled, width, min, max, storageKey],
  );

  const onHandleDoubleClick = useCallback(() => {
    if (!enabled) return;
    clearStoredWidth(storageKey);
    setMode('auto');
    measure();
  }, [enabled, storageKey, measure]);

  const onHandleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;
      const step = e.shiftKey ? 32 : 8;
      let next: number | null = null;
      if (e.key === 'ArrowLeft') next = width - step;
      else if (e.key === 'ArrowRight') next = width + step;
      else if (e.key === 'Home') next = min;
      else if (e.key === 'End') next = max;
      else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (modeRef.current === 'manual') {
          clearStoredWidth(storageKey);
          setMode('auto');
          measure();
        }
        return;
      }
      if (next == null) return;
      e.preventDefault();
      const clamped = clampWidth(next, min, max);
      setWidth(clamped);
      writeStoredWidth(storageKey, clamped);
      setMode('manual');
    },
    [enabled, width, min, max, storageKey, measure],
  );

  return {
    width,
    mode,
    dragging,
    asideRef,
    measureRef,
    onHandlePointerDown,
    onHandleDoubleClick,
    onHandleKeyDown,
  };
}
